"use client"

import styled from "@emotion/styled"
import { CarType, Desktop, Mobile, useComparison } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Button, Typography } from "@mui/material"
import { LeasingCarDto, SaleCarDto } from "api"
import Link from "next/link"
import { usePathname } from "next/navigation"

const OuterLayer = styled.div`
  position: absolute;
  top: 541px;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: sticky;
    left: 0;
    right: 0;
    z-index: 1000;
    width: 100%;
    top: 124px;
    transform: translateY(-52px);
  }
`

const HoverBox = styled.div`
  min-height: 280px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
  padding: 16px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: 104px;
    height: 104px;
    flex-direction: row;
    width: 100%;
    align-items: stretch;
  }
`

const DetailsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
    gap: 24px;
    flex-direction: row;
  }
`
const Divider = styled.div`
  background-color: ${sharedColors.lightGrey};
  height: 1px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 72px;
    width: 1px;
  }
`

const FamilyBox = styled.div<{ withChip?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: unset;
    gap: 4px;
  }
`

const DetailBox = styled.div`
  padding-top: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: column;
    align-items: unset;
  }
`

const FamilyChip = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background-color: #313237;
  width: fit-content;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 524px;
    flex-direction: row;
    gap: 25px;
  }
  justify-content: flex-end;
  flex: 1;
`

const LeftBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: center;

    width: auto;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 49px;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    max-width: 249px;
  }
`

function formatPrice(price: number): string {
  return new Intl.NumberFormat("he-IL", {
    maximumFractionDigits: 0,
  }).format(price)
}

export default function StickyHeader({
  carName,
  price,
  secondHand,
  color,
  leasing,
  year,
  description,
  disclaimer,
  onTestDriveClick,
  carData,
}: {
  carName: string
  price?: number
  secondHand?: boolean
  color?: string
  leasing?: boolean
  year?: number
  description?: string
  disclaimer?: string
  onTestDriveClick?: () => void
  carData?: SaleCarDto | LeasingCarDto
}) {
  const pathname = usePathname()
  const comparison = useComparison()

  const handleComparisonClick = () => {
    if (!carData) return

    const carType: CarType = leasing ? "leasing" : "sale"

    // Get car ID to check if already selected
    const carId = leasing
      ? `leasing-${(carData as LeasingCarDto).manufacturerCode}-${(carData as LeasingCarDto).modelCode}`
      : `sale-${(carData as SaleCarDto).vehicleNumber}`

    // If already selected, do nothing
    if (comparison.isCarSelected(carId)) {
      return
    }

    // Add car to comparison (will replace if type mismatch)
    comparison.addCar(carData, carType)
  }

  return (
    <OuterLayer>
      <Container paddingMobile>
        <HoverBox>
          <DetailsBox>
            <FamilyBox withChip>
              <FamilyChip>
                <Typography variant="body2" color="white">
                  {description}
                </Typography>
              </FamilyChip>
              <Desktop>
                <Typography variant="h3" component="h1">
                  {carName}
                </Typography>
              </Desktop>
              <Mobile>
                <Typography variant="h4" component="h1">
                  {carName}
                </Typography>
              </Mobile>
            </FamilyBox>
            <Divider />
            {leasing ? (
              <DetailBox>
                <Typography variant="body3">שנה</Typography>
                <Desktop>
                  <Typography variant="h3">{year}</Typography>
                </Desktop>
                <Mobile>
                  <Typography variant="body1">{year}</Typography>
                </Mobile>
              </DetailBox>
            ) : (
              <DetailBox>
                <Typography variant="body3">לא כולל אגרת רישוי</Typography>
                <Desktop>
                  <Typography variant="h3" component={"p"}>
                    {price && formatPrice(price)} ש"ח
                  </Typography>
                </Desktop>
                <Mobile>
                  <Typography variant="body1" component={"p"}>
                    {price && formatPrice(price)} ש"ח
                  </Typography>
                </Mobile>
              </DetailBox>
            )}
            {secondHand && (
              <>
                <Divider />
                <DetailBox>
                  <Typography variant="body3"> צבע הרכב</Typography>
                  <Desktop>
                    <Typography variant="h3">{color}</Typography>
                  </Desktop>
                  <Mobile>
                    <Typography variant="body1">{color}</Typography>
                  </Mobile>
                </DetailBox>
              </>
            )}
          </DetailsBox>
          <LeftBox>
            <ButtonsContainer>
              <StyledButton
                variant="primary"
                size="large"
                role="link"
                onClick={onTestDriveClick}
              >
                {secondHand ? "יצירת קשר" : "תיאום נסיעת מבחן"}
              </StyledButton>
              {!secondHand && (
                <StyledButton
                  variant="secondary"
                  size="large"
                  LinkComponent={Link}
                  href={`${pathname}/configuration`}
                >
                  להתאמת הרכב שלי
                </StyledButton>
              )}
              {secondHand && (
                <StyledButton
                  variant="secondary"
                  size="large"
                  onClick={handleComparisonClick}
                >
                  להשוואת רכבים
                </StyledButton>
              )}
            </ButtonsContainer>

            {!secondHand && (
              <Typography variant="body3">{disclaimer}</Typography>
            )}
          </LeftBox>
        </HoverBox>
      </Container>
    </OuterLayer>
  )
}
